import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, map, catchError, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ProjectModelWithRelations} from '@rappider/rappider-sdk';
import { getProjectModelsWithFields } from '@rappider/shared';
import { environment } from '@environment';
import { AIChatGeneratorService } from './ai-generator.service';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';

/**
 * Service for handling data model operations
 */
@Injectable({
  providedIn: 'root'
})
export class DataModelService implements OnDestroy {

  private destroy$ = new Subject<void>();
  private updateInProgress$ = new Subject<boolean>();

  constructor(
    private store: Store,
    private http: HttpClient,
    private aiChatGeneratorService: AIChatGeneratorService
  ) {
    // Constructor intentionally left empty
  }

  /**
   * Gets the current model from store by projectModelId
   * @param projectModelId - The ID of the current model
   * @returns Observable of the current model
   */
  getCurrentModel(projectModelId: string): Observable<ProjectModelWithRelations | undefined> {
    return this.store.select(getProjectModelsWithFields)
      .pipe(
        takeUntil(this.destroy$),
        map((projectModels: ProjectModelWithRelations[] | undefined) =>
          projectModels?.find(model => model.id === projectModelId)
        )
      );
  }

  /**
   * Gets formatted fields combining current model and response data
   * @param currentModel - The current project model
   * @param actionResponseData - Response data from action execution
   * @returns Array of formatted field objects with status indicators
   */
  getFormattedFields(
    currentModel: ProjectModelWithRelations | undefined,
    actionResponseData?: Record<string, unknown> | unknown[]
  ): unknown[] {

    if (!currentModel?.fields) {
      if (actionResponseData) {
        return this.formatResponseFields(actionResponseData);
      }
      return [];
    }

    const currentFields = currentModel.fields
      .filter(field => field.isUpdatable)
      .map(field => ({
        name: field.name || '-',
        type: field.type || '-',
        description: field.description || '-',
        status: this.getFieldStatus(field.name || '-', currentModel, actionResponseData)
      }));

    const responseFields: unknown[] = [];
    if (actionResponseData) {
      const formattedResponseFields = this.formatResponseFields(actionResponseData);
      const currentFieldNames = currentFields.map(field => field.name);

      formattedResponseFields.forEach(field => {
        const fieldObj = field as Record<string, unknown>;
        if (!currentFieldNames.includes(fieldObj.name as string)) {
          responseFields.push(field);
        }
      });
    }

    return [...currentFields, ...responseFields];
  }

  /**
   * Formats response fields from the new array format
   * @param actionResponseData - Response data in array format
   * @returns Array of formatted field objects
   */
  private formatResponseFields(actionResponseData: Record<string, unknown> | unknown[]): unknown[] {
    if (Array.isArray(actionResponseData)) {
      return actionResponseData.map(fieldObj => {
        const field = fieldObj as Record<string, unknown>;
        const fieldName = Object.keys(field).find(key => key !== 'description');
        const fieldValue = fieldName ? field[fieldName] : null;

        return {
          name: fieldName || '-',
          type: this.getFieldTypeFromValue(fieldValue),
          description: field.description || 'Field from AI response',
          status: 'new'
        };
      });
    }

    return Object.keys(actionResponseData).map(key => ({
      name: key,
      type: this.getFieldTypeFromValue(actionResponseData[key]),
      description: 'Field from AI response',
      status: 'new'
    }));
  }

  /**
   * Determines field type from value
   * @param value - Field value
   * @returns Field type string
   */
  private getFieldTypeFromValue(value: unknown): string {
    if (typeof value === 'string') {
      return 'string';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    if (typeof value === 'object' && value !== null) {
      return 'object';
    }
    return 'unknown';
  }

  /**
   * Gets field names from response data (handles both array and object formats)
   * @param actionResponseData - Response data from action execution
   * @returns Array of field names
   */
  private getResponseFieldNames(actionResponseData: Record<string, unknown> | unknown[]): string[] {
    if (Array.isArray(actionResponseData)) {
      return actionResponseData.map(fieldObj => {
        const field = fieldObj as Record<string, unknown>;
        return Object.keys(field).find(key => key !== 'description') || '';
      }).filter(name => name !== '');
    }

    return Object.keys(actionResponseData);
  }

  /**
   * Determines the status of a field by comparing current model with response data
   * @param fieldName - Name of the field to check
   * @param currentModel - Current project model
   * @param actionResponseData - Response data from action execution
   * @returns Field status: 'new', 'deleted', or 'unchanged'
   */
  private getFieldStatus(
    fieldName: string,
    currentModel: ProjectModelWithRelations,
    actionResponseData?: Record<string, unknown> | unknown[]
  ): 'new' | 'deleted' | 'unchanged' {
    if (!actionResponseData) {
      return 'unchanged';
    }

    const responseKeys = this.getResponseFieldNames(actionResponseData);
    const currentModelKeys = currentModel?.fields
      ?.filter(field => field.isUpdatable)
      ?.map(field => field.name) || [];

    // Field exists in current model but not in response = deleted
    if (currentModelKeys.includes(fieldName) && !responseKeys.includes(fieldName)) {
      return 'deleted';
    }

    // Field exists in both current model and response = unchanged
    if (currentModelKeys.includes(fieldName) && responseKeys.includes(fieldName)) {
      return 'unchanged';
    }

    // Field exists in response but not in current model = new
    if (!currentModelKeys.includes(fieldName) && responseKeys.includes(fieldName)) {
      return 'new';
    }

    return 'unchanged';
  }

  /**
   * Update data model from AI response if available
   * @param responseData - AI response data that may contain model updates
   * @returns Observable of model update result
   */
  updateProcessDataModelFromResponse(responseData: unknown): Observable<unknown> {
    return this.aiChatGeneratorService.processAIResponseActions(responseData);
  }


  updateDataModel(
    projectModelId: string,
    dataModelSampleJSON: Record<string, unknown> | unknown[]
  ): Observable<unknown> {
    const url = `${environment.apiBaseUrl}/project-models/update-fields-by-json-data`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body = {
      projectModelId: projectModelId,
      dataModelSampleJSON: dataModelSampleJSON
    };
    this.updateInProgress$.next(true);

    return this.http.post(url, body, { headers }).pipe(
      switchMap((result) => {
        this.store.dispatch(new ProjectModelActions.GetProjectModels());
        return of(result);
      }),
      catchError(() => of({ error: 'Model update failed' })),
      tap(() => this.updateInProgress$.next(false))
    );
  }

  /**
   * Subscribe to update progress state
   * @returns Observable of update progress boolean
   */
  subscribeToUpdateProgress(): Observable<boolean> {
    return this.updateInProgress$.asObservable().pipe(
      takeUntil(this.destroy$)
    );
  }

  /**
   * Checks if there are missing models for relations by comparing with existing project models
   * @param responseData - AI response data containing relations
   * @param existingProjectModels - Array of existing project models
   * @returns True if any relation has missing source or target models
   */
  hasMissingModelsForRelations(
    responseData: unknown,
    existingProjectModels: ProjectModelWithRelations[]
  ): boolean {
    if (!responseData || typeof responseData !== 'object') {
      return false;
    }

    const dataObj = responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];
    const models = generated?.models as unknown[] || [];

    if (!relations || relations.length === 0) {
      return false;
    }

    // Get model names from AI response
    const aiModelNames = models.map((model: unknown) => {
      const modelObj = model as Record<string, unknown>;
      return (modelObj.modelName as string) || (modelObj.name as string) || 'Generated Model';
    });

    // Get model names from existing project models
    const existingModelNames = existingProjectModels.map(model => model.name);

    // Combine both lists
    const allModelNames = [...aiModelNames, ...existingModelNames];

    return relations.some((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      const sourceModel = rel.sourceModel as string;
      const targetModel = rel.targetModel as string;

      return !allModelNames.includes(sourceModel) || !allModelNames.includes(targetModel);
    });
  }

  /**
   * Gets missing model names for relations
   * @param responseData - AI response data containing relations
   * @param existingProjectModels - Array of existing project models
   * @returns Array of missing model names
   */
  getMissingModelNames(
    responseData: unknown,
    existingProjectModels: ProjectModelWithRelations[]
  ): string[] {
    if (!responseData || typeof responseData !== 'object') {
      return [];
    }

    const dataObj = responseData as Record<string, unknown>;
    const generated = dataObj.generated as Record<string, unknown>;
    const relations = generated?.relations as unknown[] || [];
    const models = generated?.models as unknown[] || [];

    if (!relations || relations.length === 0) {
      return [];
    }

    // Get model names from AI response
    const aiModelNames = models.map((model: unknown) => {
      const modelObj = model as Record<string, unknown>;
      return (modelObj.modelName as string) || (modelObj.name as string) || 'Generated Model';
    });

    // Get model names from existing project models
    const existingModelNames = existingProjectModels.map(model => model.name);

    // Combine both lists
    const allModelNames = [...aiModelNames, ...existingModelNames];

    const missingModels = new Set<string>();

    relations.forEach((relation: unknown) => {
      const rel = relation as Record<string, unknown>;
      const sourceModel = rel.sourceModel as string;
      const targetModel = rel.targetModel as string;

      if (sourceModel && !allModelNames.includes(sourceModel)) {
        missingModels.add(sourceModel);
      }
      if (targetModel && !allModelNames.includes(targetModel)) {
        missingModels.add(targetModel);
      }
    });

    return Array.from(missingModels);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.updateInProgress$.complete();
  }
}
